/**
 * テンプレート一覧の共通表示コンポーネント
 *
 * 検索フィルタ + Collapsible 展開で食品詳細を表示する。
 * アクション部分は renderActions で各利用側がカスタマイズする。
 */

import { ChevronDown, Search } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import type { FoodTemplate } from '@/api/types/nutrition'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { scaleQuantity } from './utils'

type TemplatePickerListProps = {
  templates: FoodTemplate[]
  /** 各テンプレート行の右側に表示するアクション要素 */
  renderActions: (template: FoodTemplate) => ReactNode
  /** チェックボックスなど、トリガー左側に表示する要素 */
  renderPrefix?: (template: FoodTemplate) => ReactNode
  /** カロリー表示をカスタマイズ（倍率反映など） */
  caloriesLabel?: (template: FoodTemplate) => string
  /** テンプレート名クリック時（Chevron以外のタップ。省略時は何もしない） */
  onItemClick?: (template: FoodTemplate) => void
  /** 検索バーを非表示にする */
  hideSearch?: boolean
  /** リストの最大高さ（デフォルト: max-h-60） */
  maxHeight?: string
  /** 空状態のメッセージ */
  emptyMessage?: string
}

export function TemplatePickerList({
  templates,
  renderActions,
  renderPrefix,
  caloriesLabel,
  onItemClick,
  hideSearch,
  maxHeight = 'max-h-60',
  emptyMessage = 'テンプレートがありません',
}: TemplatePickerListProps) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return templates
    return templates.filter(
      (t) => t.name.toLowerCase().includes(q) || t.items.some((item) => item.name.toLowerCase().includes(q)),
    )
  }, [templates, search])

  return (
    <div className="space-y-2">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="検索..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      )}

      <div className={`space-y-1.5 overflow-y-auto ${maxHeight}`}>
        {filtered.map((t) => (
          <Collapsible key={t.id}>
            <div className="rounded-md border border-border transition-colors hover:bg-accent/30">
              <div className="flex items-center gap-2 px-3 py-2">
                {renderPrefix?.(t)}
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group -my-2 flex shrink-0 items-center justify-center px-1 py-3 text-muted-foreground"
                    aria-label="詳細を開閉"
                  >
                    <ChevronDown className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                {onItemClick ? (
                  <button type="button" className="min-w-0 flex-1 p-0 text-left" onClick={() => onItemClick(t)}>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{t.name}</span>
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {caloriesLabel ? caloriesLabel(t) : `${Math.round(t.total_calories)}kcal`}
                      </span>
                    </div>
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{t.name}</span>
                    <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                      {caloriesLabel ? caloriesLabel(t) : `${Math.round(t.total_calories)}kcal`}
                    </span>
                  </div>
                )}
                {renderActions(t)}
              </div>
              <CollapsibleContent>
                <div className="border-t border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-1 pl-4 text-xs">食品</TableHead>
                        <TableHead className="py-1 text-right text-xs tabular-nums">kcal</TableHead>
                        <TableHead className="py-1 text-right text-xs tabular-nums">P</TableHead>
                        <TableHead className="py-1 text-right text-xs tabular-nums">F</TableHead>
                        <TableHead className="py-1 text-right text-xs tabular-nums">C</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {t.items.map((item) => {
                        const displayQuantity = scaleQuantity(item.quantity, item.scale ?? 1)
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="py-1 pl-4 text-xs">
                              {item.name}
                              {displayQuantity && (
                                <span className="ml-1 text-muted-foreground">({displayQuantity})</span>
                              )}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs tabular-nums text-muted-foreground">
                              {Math.round(item.calories)}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs tabular-nums text-muted-foreground">
                              {item.protein_g.toFixed(0)}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs tabular-nums text-muted-foreground">
                              {item.fat_g.toFixed(0)}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs tabular-nums text-muted-foreground">
                              {item.carb_g.toFixed(0)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
                {/* 合計行 */}
                <div className="border-t border-border">
                  <Table>
                    <TableBody>
                      <TableRow className="font-medium">
                        <TableCell className="py-1 pl-4 text-xs">合計</TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums">
                          {Math.round(t.total_calories)}
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums text-blue-400">
                          {t.total_protein_g.toFixed(0)}
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums text-yellow-400">
                          {t.total_fat_g.toFixed(0)}
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums text-green-400">
                          {t.total_carb_g.toFixed(0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        ))}
        {filtered.length === 0 && templates.length > 0 && (
          <p className="py-2 text-center text-xs text-muted-foreground">一致するお気に入りがありません</p>
        )}
        {templates.length === 0 && <p className="py-2 text-center text-xs text-muted-foreground">{emptyMessage}</p>}
      </div>
    </div>
  )
}
